import { SupportInbox } from './components/SupportInbox';
import { ChatOperations } from './components/ChatOperations';
import { RelationshipLibrary } from './components/RelationshipLibrary';
import { PhotoArchives } from './components/PhotoArchives';
import { HeartBankPanel } from './components/HeartBankPanel';
/**
 * ReKindle Admin Panel
 * Main application component
 */

import { Admin, Resource, CustomRoutes } from 'react-admin';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import StyleIcon from '@mui/icons-material/Style';
import CategoryIcon from '@mui/icons-material/Category';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import EventIcon from '@mui/icons-material/Event';
import ChatIcon from '@mui/icons-material/Chat';
import PaymentIcon from '@mui/icons-material/Payment';
import HistoryIcon from '@mui/icons-material/History';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import QuizIcon from '@mui/icons-material/Quiz';
import ShieldIcon from '@mui/icons-material/Shield';

import { authProvider } from './providers/authProvider';
import { dataProvider } from './providers/dataProvider';

import { Dashboard } from './components/Dashboard';
import { LoginPage } from './components/LoginPage';
import { CustomLayout } from './components/CustomLayout';
import { Health } from './components/Health';

// User Management
import { UserList } from './components/UserList';
import { UserShow } from './components/UserShow';
import { NotificationBroadcast } from './components/NotificationBroadcast';
import { NotificationTranslationManagement } from './components/NotificationTranslationManagement';
import { UserUsageShow } from './components/UserUsageShow';
import { BonusUsageList } from './components/BonusUsageList';

// AI Features
import { ChatConversationList } from './components/ChatConversationList';
import { ChatConversationShow } from './components/ChatConversationShow';
import { AICostList } from './components/AICostList';
import { AICostUserDetail } from './components/AICostUserDetail';
import { AIConfigPanel } from './components/AIConfigPanel';
import { ModerationLogList } from './components/ModerationLogList';
import { ToolStatsList } from './components/ToolStatsList';

// Content Management - Cards
import { CardList } from './components/CardList';
import { CardEdit } from './components/CardEdit';
import { CardCreate } from './components/CardCreate';

// Content Management - Categories
import { CategoryList } from './components/CategoryList';
import { CategoryEdit } from './components/CategoryEdit';
import { CategoryCreate } from './components/CategoryCreate';

// Content Management - Suggestions
import { SuggestionList } from './components/SuggestionList';
import { SuggestionShow } from './components/SuggestionShow';

// Content Management - Questions
import { QuestionList } from './components/QuestionList';
import { QuestionShow } from './components/QuestionShow';

// Content Management - Important Dates
import { ImportantDateList } from './components/ImportantDateList';
import { ImportantDateEdit } from './components/ImportantDateEdit';
import { ImportantDateCreate } from './components/ImportantDateCreate';

// Business
import { SubscriptionList } from './components/SubscriptionList';
import { AuditLogList } from './components/AuditLogList';
import { CronJobs } from './components/CronJobs';
import { lightTheme, darkTheme } from './theme';

function AdminApp() {
  return (
    <Admin
      authProvider={authProvider}
      dataProvider={dataProvider}
      dashboard={Dashboard}
      loginPage={LoginPage}
      layout={CustomLayout}
      theme={lightTheme}
      darkTheme={darkTheme}
      title="ReKindle Admin"
      requireAuth
    >
      <Resource name="photo-exports" list={PhotoArchives} options={{ label: 'Photo Archives' }} />
      {/* User Management */}
      <Resource
        name="users"
        list={UserList}
        show={UserShow}
        icon={PersonIcon}
        options={{ label: 'Users' }}
      />

      <Resource
        name="bonus-usage"
        list={BonusUsageList}
        icon={CardGiftcardIcon}
        options={{ label: 'Bonus Usage' }}
      />

      {/* AI Features */}
      <Resource
        name="content/conversations"
        list={ChatConversationList}
        show={ChatConversationShow}
        icon={ChatIcon}
        options={{ label: 'Conversations' }}
      />
      <Resource
        name="moderation-logs"
        list={ModerationLogList}
        icon={ShieldIcon}
        options={{ label: 'Moderation Logs' }}
      />

      {/* Content Management */}
      <Resource
        name="content/cards"
        list={CardList}
        edit={CardEdit}
        create={CardCreate}
        icon={StyleIcon}
        options={{ label: 'Cards' }}
      />
      <Resource
        name="content/questions"
        list={QuestionList}
        show={QuestionShow}
        icon={QuizIcon}
        options={{ label: 'Questions' }}
      />
      <Resource
        name="content/categories"
        list={CategoryList}
        edit={CategoryEdit}
        create={CategoryCreate}
        icon={CategoryIcon}
        options={{ label: 'Categories' }}
      />
      <Resource
        name="content/suggestions"
        list={SuggestionList}
        show={SuggestionShow}
        icon={LightbulbIcon}
        options={{ label: 'Suggestions' }}
      />
      <Resource
        name="content/important-dates"
        list={ImportantDateList}
        edit={ImportantDateEdit}
        create={ImportantDateCreate}
        icon={EventIcon}
        options={{ label: 'Important Dates' }}
      />

      {/* Subscriptions & Audit */}
      <Resource
        name="content/subscriptions"
        list={SubscriptionList}
        icon={PaymentIcon}
        options={{ label: 'Subscriptions' }}
      />
      <Resource
        name="audit-logs"
        list={AuditLogList}
        icon={HistoryIcon}
        options={{ label: 'Audit Logs' }}
      />

      <CustomRoutes>
        <Route path="/support" element={<SupportInbox />} />
        <Route path="/chat-operations" element={<ChatOperations />} />
        <Route path="/relationship-library" element={<RelationshipLibrary />} />
        <Route path="/heart-bank" element={<HeartBankPanel />} />
        <Route path="/broadcast" element={<NotificationBroadcast />} />
        <Route path="/notification-translations" element={<NotificationTranslationManagement />} />
        <Route path="/users/:userId/usage" element={<UserUsageShow />} />
        <Route path="/ai-costs" element={<AICostList />} />
        <Route path="/ai-costs/:userId" element={<AICostUserDetail />} />
        <Route path="/ai-config" element={<AIConfigPanel />} />
        <Route path="/tool-stats" element={<ToolStatsList />} />
        <Route path="/cron-jobs" element={<CronJobs />} />
      </CustomRoutes>
    </Admin>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/health" element={<Health />} />
        <Route path="/*" element={<AdminApp />} />
      </Routes>
    </BrowserRouter>
  );
}
